import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import {
  BeforeInstallPromptEvent,
  PWA_INSTALLED_EVENT,
  PWA_INSTALLED_KEY,
  PWA_PROMPT_READY_EVENT,
} from '@/lib/pwaInstall';

const DISMISS_KEY = 'pulp-pwa-install-prompt-dismissed';

export interface UsePwaInstallResult {
  canInstall: boolean;
  canPrompt: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  prompt: () => Promise<boolean>;
  dismiss: () => void;
  isDismissed: boolean;
}

let capturedPrompt: BeforeInstallPromptEvent | null =
  typeof window !== 'undefined' ? window.__pulpPwaDeferredPrompt || null : null;

function handleBeforeInstallPrompt(event: BeforeInstallPromptEvent) {
  event.preventDefault();
  capturedPrompt = event;
  if (typeof window !== 'undefined') {
    window.__pulpPwaDeferredPrompt = event;
    window.dispatchEvent(new CustomEvent(PWA_PROMPT_READY_EVENT));
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
}

function getIsInstalled(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  if (window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true) {
    return true;
  }
  try {
    return localStorage.getItem(PWA_INSTALLED_KEY) === '1';
  } catch {
    return false;
  }
}

function getIsIOS(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent.toLowerCase();
  const isAppleMobile = /iphone|ipad|ipod/.test(ua);
  const isIPadDesktop =
    ua.includes('macintosh') && 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 1;
  return (isAppleMobile || isIPadDesktop) && !getIsInstalled();
}

function subscribePwaPrompt(callback: () => void) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(PWA_PROMPT_READY_EVENT, callback);
  return () => window.removeEventListener(PWA_PROMPT_READY_EVENT, callback);
}

function getPwaPrompt() {
  return capturedPrompt;
}

function subscribePwaInstalled(callback: () => void) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(PWA_INSTALLED_EVENT, callback);
  return () => window.removeEventListener(PWA_INSTALLED_EVENT, callback);
}

export function usePwaInstall(): UsePwaInstallResult {
  const deferredPrompt = useSyncExternalStore(subscribePwaPrompt, getPwaPrompt, () => null);
  const isInstalled = useSyncExternalStore(subscribePwaInstalled, getIsInstalled, () => false);
  const isIOS = useMemo(() => getIsIOS(), []);
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(DISMISS_KEY) === '1';
  });

  const prompt = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      capturedPrompt = null;
      if (typeof window !== 'undefined') {
        window.__pulpPwaDeferredPrompt = null;
        window.dispatchEvent(new CustomEvent(PWA_PROMPT_READY_EVENT));
      }
      return true;
    }
    return false;
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem(DISMISS_KEY, '1');
    }
  }, []);

  const canPrompt = !!deferredPrompt;

  return {
    canInstall: (canPrompt || isIOS) && !isInstalled && !isDismissed,
    canPrompt,
    isInstalled,
    isIOS,
    prompt,
    dismiss,
    isDismissed,
  };
}
